import { useState, useEffect, useCallback } from 'react';
import cloudflareAPI from '../../services/api/cloudflareAPI';
import type { SubscriptionPlan, PaymentRequest } from '../../services/api/cloudflareAPI';
import paymentService from '../../services/payment/paymentService';
import { logger } from '../../utils/logger';
import { FALLBACK_PRICES } from '../../config/tierPricing';

interface UseSubscriptionPlansResult {
  plans: SubscriptionPlan[];
  loading: boolean;
  error: string | null;
  refreshPlans: () => Promise<void>;
}

interface UsePaymentResult {
  createTransaction: (request: PaymentRequest) => Promise<string>; // Returns redirect URL
  loading: boolean;
  error: string | null;
}

export function useSubscriptionPlans(): UseSubscriptionPlansResult {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = useCallback(async () => {
    try {
      setError(null);
      
      // Try to fetch from API, fallback to mock data if it fails
      try {
        const plansData = await cloudflareAPI.getSubscriptionPlans();
        setPlans(plansData);
      } catch (apiError) {
        logger.warn('API not available, using mock subscription plans', { error: apiError });
        
        // Fallback: try to build plans from SubscriptionService cached tier configs
        const { SubscriptionService } = await import('../../services/subscription/subscriptionService');
        await SubscriptionService.fetchTierConfigs();
        const freeLimits = SubscriptionService.getSubscriptionLimits('FREE');
        const proLimits = SubscriptionService.getSubscriptionLimits('PROFESSIONAL');

        // Fallback plans when API is unreachable — prices are overridden by API when available
        const fallbackPlans: SubscriptionPlan[] = [
          {
            id: 'free',
            name: 'Free Trader',
            nameId: 'Gratis',
            price: 0,
            currency: 'IDR',
            period: 'bulan',
            description: 'Sempurna untuk pemula yang ingin mencoba',
            features: freeLimits.features.map(f => f),
            featuresId: freeLimits.features.map(f => f),
            limits: {
              signalsPerDay: freeLimits.signalsPerDay,
              instruments: freeLimits.instrumentCount
            }
          },
          {
            id: 'professional',
            name: 'Professional Trader',
            nameId: 'Profesional',
            price: FALLBACK_PRICES.PROFESSIONAL,
            currency: 'IDR',
            period: 'bulan',
            description: 'Solusi lengkap untuk investor saham serius',
            features: proLimits.features.map(f => f),
            featuresId: proLimits.features.map(f => f),
            limits: {
              signalsPerDay: proLimits.signalsPerDay,
              instruments: proLimits.instrumentCount
            },
            popular: true
          }
        ];

        setPlans(fallbackPlans);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch subscription plans';
      setError(errorMessage);
      logger.error('Subscription plans fetch error', { error: err });
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshPlans = useCallback(async () => {
    setLoading(true);
    await fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    void fetchPlans();
  }, [fetchPlans]);

  return {
    plans,
    loading,
    error,
    refreshPlans
  };
}

/**
 * Production-ready payment hook using Duitku redirect flow
 *
 * Flow:
 * 1. Call Cloudflare Workers backend to create Duitku transaction
 * 2. Backend returns payment_url from Duitku
 * 3. Redirect browser to payment_url
 */
export function usePayment(): UsePaymentResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createTransaction = useCallback(async (request: PaymentRequest): Promise<string> => {
    setLoading(true);
    setError(null);

    try {
      // Primary: Use Cloudflare Workers backend API
      try {
        const response = await cloudflareAPI.createPaymentTransaction(request);
        return response.payment_url;
      } catch (apiError) {
        logger.warn('Cloudflare API payment creation failed', { error: apiError });

        // Fallback: Use paymentService directly (also calls backend)
        try {
          await paymentService.processPayment({
            userId: request.userId,
            planId: request.planId,
            customerDetails: request.customerDetails
          });
          // processPayment redirects the browser, so we won't reach here normally
          return 'redirect_in_progress';
        } catch (fallbackError) {
          logger.error('Payment service fallback also failed', { error: fallbackError });

          const isProduction = import.meta.env.VITE_ENVIRONMENT === 'production';
          if (!isProduction) {
            logger.warn('Development/Staging mode: Payment service unavailable');
            alert(`DEVELOPMENT MODE: Payment services unavailable\n\nPlan: ${request.planId}\nCustomer: ${request.customerDetails.name}\n\nPlease check API configuration.`);
            return 'development_mode_fallback';
          } else {
            throw new Error('Payment service unavailable. Please try again later or contact support.');
          }
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create payment transaction';
      setError(errorMessage);
      logger.error('Payment creation error', { error: err });
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createTransaction,
    loading,
    error
  };
}

// Utility function to format Indonesian currency
export function formatIDR(amount: number): string {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

// Utility function to validate Indonesian phone number
export function validateIndonesianPhone(phone: string): boolean {
  const phoneRegex = /^(\+62|62|0)[0-9]{9,13}$/;
  return phoneRegex.test(phone.replace(/\s|-/g, ''));
}

// Utility function to format Indonesian phone number
export function formatIndonesianPhone(phone: string): string {
  const cleaned = phone.replace(/\s|-/g, '');
  
  if (cleaned.startsWith('+62')) {
    return cleaned;
  } else if (cleaned.startsWith('62')) {
    return `+${cleaned}`;
  } else if (cleaned.startsWith('08')) {
    return `+62${cleaned.substring(1)}`;
  } else if (cleaned.startsWith('8')) {
    return `+62${cleaned}`;
  }
  
  return phone;
}